import { useInfiniteQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import { useEffect, useMemo } from "react";
import { fetchData } from "@/utils/api-handler";
import { useProtectedQuery } from "@/hooks/use-protected-query";

interface CommandAgentList {
    item_count: number;
    page_count: number;
    items: any[];
}

export function useCommandAgents(filters?: { is_active?: boolean; search?: string; limit?: number }) {
    const { hasPermission } = useProtectedQuery({
        queryKey: ["_gated_list_command_agents"],
        queryFn: async () => null,
        requiredPermissions: ["LIST_COMMAND_AGENTS"],
        enabled: false,
    });

    const { ref: loadMoreRef, inView } = useInView({
        threshold: 0,
        triggerOnce: false,
    });

    const {
        data: agentsData,
        isLoading: isLoadingAgents,
        isFetching: isFetchingAgents,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useInfiniteQuery<CommandAgentList>({
        queryKey: ["command-agents-sidebar", filters?.is_active, filters?.search, filters?.limit],
        initialPageParam: 1,
        // Keep sidebar agents warm so opening the panel doesn't flash empty skeletons.
        staleTime: 60_000,
        gcTime: 5 * 60_000,
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        queryFn: async ({ pageParam }) => {
            const params = new URLSearchParams({
                page: String(pageParam),
                limit: String(filters?.limit || 50),
                sort_by: "created_at",
                sort_order: "desc",
            });
            if (filters?.is_active !== undefined) {
                params.set("is_active", String(filters.is_active));
            }
            if (filters?.search?.trim()) {
                params.set("search", filters.search.trim());
            }
            const response = await fetchData(
                `/api/command-team/agents?${params.toString()}`
            );
            return response;
        },
        getNextPageParam: (lastPage, pages) => {
            if (pages.length < lastPage.page_count) {
                return pages.length + 1;
            }
            return undefined;
        },
        enabled: hasPermission,
    });

    const allAgents = useMemo(() => {
        return agentsData?.pages.flatMap((page) => page.items) ?? [];
    }, [agentsData]);

    // Load more when scrolling to bottom
    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    return {
        allAgents,
        isLoadingAgents,
        isFetchingAgents,
        isFetchingNextPage,
        hasNextPage,
        loadMoreRef,
    };
}

